import React, { useState, useEffect, useRef } from 'react';
import { SlIcon } from '@shoelace-style/shoelace/dist/react';
import Select from 'react-select';
import { Toast } from '../../../shared/Toast';

const ContentWriterAssignmentModal = ({ 
    isOpen, 
    onClose, 
    onConfirm, 
    contentWriters, 
    currentContentWriter = null,
    currentDescription = '',
    creativeId,
    content_id,
    hideWriterSelect = false
}) => {
    const [selectedContentWriter, setSelectedContentWriter] = useState(null);
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const hasInitialized = useRef(false);

    useEffect(() => {
        if (isOpen && !hasInitialized.current) {
            if (currentContentWriter) {
                setSelectedContentWriter({
                    value: currentContentWriter.content_writer_id || currentContentWriter.id,
                    label: currentContentWriter.name || currentContentWriter.content_writer_name
                });
            } else {
                setSelectedContentWriter(null);
            }
            setDescription(currentDescription || '');
            hasInitialized.current = true;
        } else if (!isOpen) {
            hasInitialized.current = false;
            setSelectedContentWriter(null);
            setDescription('');
        }
    }, [isOpen, currentContentWriter, currentDescription]);

    const handleConfirm = async () => {
        if (!selectedContentWriter && !hideWriterSelect) {
            Toast("info",'Please select a content writer');
            return;
        }
        if(!description.trim()){
            Toast("info","Please Enter Description")
            return;
        }
        setLoading(true);
        try {
            console.log(selectedContentWriter,description);
            await onConfirm({
                content_id,
                creative_id: creativeId,
                content_writer_id: selectedContentWriter ? selectedContentWriter.value : undefined,
                content_writer_name: selectedContentWriter ? selectedContentWriter.label : undefined,
                description: description.trim()
            });
            onClose();
        } catch (error) {
            console.error('Error assigning content writer:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <SlIcon library="lucide" name="edit-3" className="text-blue-600" />
                        {currentContentWriter ? 'Edit Content Writer' : 'Assign Content Writer'}
                    </h3>
                    <button
                        onClick={handleClose}
                        disabled={loading}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <SlIcon library="lucide" name="x" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4 text-md">
                    {!hideWriterSelect && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Content Writer *
                            </label>
                            <Select
                                options={contentWriters}
                                value={selectedContentWriter}
                                onChange={setSelectedContentWriter}
                                placeholder="Select Content Writer"
                                classNamePrefix="react-select"
                                isDisabled={loading}
                                className="w-full"
                            />
                        </div>
                    )}

                    {/* Description Field */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description *
                        </label>
                        <textarea
                            required="please enter description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Enter description or instructions for the content writer..."
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                            rows={20}
                            disabled={loading}
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
                    <button
                        onClick={handleClose}
                        disabled={loading}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={loading || (!selectedContentWriter && !hideWriterSelect)}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center gap-2"
                    >
                        {loading ? (
                            <>
                                <SlIcon library="lucide" name="loader-2" className="animate-spin" />
                                Assigning...
                            </>
                        ) : (
                            <>
                                <SlIcon library="lucide" name="check" />
                                {currentContentWriter ? 'Update' : 'Assign'}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ContentWriterAssignmentModal;
